import { type DisableAutoFetchFieldValue, type PortalSettings, type FieldValues } from '@gorilla/portal-shared/src/schemas';
import { type DisableAutoFetchFieldDescriptor, type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Icon, Toggle, Text, Tooltip } from '@vibe/core';
import { Retry as RetryIcon, LearnMore as LearnMoreIcon } from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';

type DisableAutoFetchFieldProps = {
  value: DisableAutoFetchFieldValue | undefined;
  fieldDescriptor: DisableAutoFetchFieldDescriptor;
  portalSettings: PortalSettings;
  sectionDescriptor: SectionDescriptor;
  sectionFieldValues: FieldValues;
  onChange: (value: DisableAutoFetchFieldValue | undefined) => void;
  clientBoardSubitemsBoardId: number | null;
};

export function DisableAutoFetchField({
  value,
  portalSettings,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
  onChange,
}: DisableAutoFetchFieldProps) {
  const { board, isLoading, boardId } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (!boardId || isLoading || !board) {
    return null;
  }

  return (
    <Box
      border={true}
      borderColor="layoutBorderColor"
      className="section__field section__field--disableautofetch section__field--nomargin item--actions--wrapper"
    >
      <Box backgroundColor="greyBackgroundColor">
        <Box padding="medium">
          <Flex
            gap={24}
            style={{
              height: '30px',
            }}
          >
            <Flex gap={8}>
              <div
                style={{
                  position: 'relative',
                  marginRight: '2px',
                  transform: 'translate3d(1px,0,0)',
                }}
              >
                <Icon icon={RetryIcon} size={19} />
                <div
                  style={{
                    position: 'absolute',
                    top: '9px',
                    left: '2.5px',
                    transform: 'rotate(-45deg)',
                    width: '14px',
                    height: '1.5px',
                    backgroundColor: '#323338',
                  }}
                />
              </div>
              <Text type="text2" weight="bold" align="start" ellipsis={true}>
                Disable auto fetching
              </Text>
              <div>
                <Tooltip
                  title="What's this?"
                  content="The portal will not automatically fetch and update data from the board after interaction, including item editing or action buttons."
                  moveBy={{ main: 5, secondary: -15 }}
                  position="right"
                  showOnDialogEnter={false}
                >
                  <Icon icon={LearnMoreIcon} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                </Tooltip>
              </div>
            </Flex>
            <Box style={{ marginLeft: 'auto' }}>
              <Flex
                justify="start"
                gap={10}
                style={{
                  flex: 1,
                }}
              >
                <Box
                  style={{
                    marginRight: 'auto',
                  }}
                >
                  <Flex style={{ margin: '0 -8px' }}>
                    <Toggle
                      offOverrideText=""
                      onOverrideText=""
                      size="small"
                      isSelected={!!value?.enabled}
                      onChange={(isSelected) => {
                        onChange({ ...(value || {}), enabled: isSelected });
                      }}
                    />
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
}
